import React, { useCallback, useMemo, useState } from 'react';

import { useQueryClient, useQuery as useTanstackQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import qs from 'qs';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';

import { Box, Container } from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useDeleteVoucher } from '~/hooks/apis/promo/useDeleteVoucher';
import useCurrentTenant from '~/hooks/useCurrentTenant';
import useQuery from '~/hooks/useQuery';

import { ListVouchersQuery, ListVouchersResponse, Voucher, listVouchers } from '~/services/PromoService';
import UsersService from '~/services/UsersService';

import VouchersTab from './VouchersTab';

export default function VouchersContainer() {
  const query = useQuery();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const tenant = useCurrentTenant();
  const deleteVoucherMutation = useDeleteVoucher();

  const [pageNum, setPageNum] = useState<number>(query.get('page_num') ? Number(query.get('page_num')) : 0);
  const [pageSize, setPageSize] = useState<number>(query.get('page_size') ? Number(query.get('page_size')) : 20);
  const [voucherName, setVoucherName] = useState<string>(query.get('voucher_name') || '');
  const [userEmail, setUserEmail] = useState<string>(query.get('user_email') || '');
  const [isActive, setIsActive] = useState<'all' | 'active' | 'inactive'>(() => {
    const activeParam = query.get('is_active');
    if (activeParam === 'active' || activeParam === 'inactive') {
      return activeParam;
    }
    return 'all';
  });

  const isActiveParam = isActive === 'active' ? true : isActive === 'inactive' ? false : undefined;
  const normalizedEmail = userEmail.trim().toLowerCase();

  const { data: userMatches = [] } = useTanstackQuery({
    queryKey: ['voucher-user-search', normalizedEmail, tenant.tenant.brand],
    queryFn: async () => {
      if (!normalizedEmail) {
        return [];
      }
      return await UsersService.getUsers({ filter: normalizedEmail, limit: 10, brand: tenant.tenant.brand });
    },
    enabled: Boolean(normalizedEmail?.includes('@')),
    refetchOnWindowFocus: false,
  });

  const resolvedUserId = useMemo(() => {
    if (!normalizedEmail) {
      return undefined;
    }
    const exact = (userMatches as Array<{ id_member?: string; email?: string; email_address?: string }>).find(
      (user) => {
        const email = user.email || user.email_address || '';
        return email.toLowerCase() === normalizedEmail;
      },
    );
    return exact?.id_member;
  }, [normalizedEmail, userMatches]);

  const queryParams: ListVouchersQuery = useMemo(() => {
    return {
      page: pageNum,
      limit: pageSize,
      brand: tenant.tenant.brand,
      ...(voucherName ? { voucher_name: voucherName } : {}),
      ...(resolvedUserId ? { user_id: resolvedUserId } : {}),
      ...(isActiveParam !== undefined ? { is_active: isActiveParam } : {}),
    } as ListVouchersQuery;
  }, [pageNum, pageSize, voucherName, resolvedUserId, isActiveParam]);

  const setQueryParams = useCallback(() => {
    const searchParams = qs.stringify({
      page_num: pageNum.toString(),
      page_size: pageSize.toString(),
      voucher_name: voucherName || undefined,
      user_email: userEmail || undefined,
      is_active: isActive,
    });
    history.push({ search: `?${searchParams}` });
  }, [history, pageNum, pageSize, voucherName, userEmail, isActive]);

  const shouldFetchVouchers = !normalizedEmail || Boolean(resolvedUserId);
  const { data, isLoading } = useTanstackQuery<ListVouchersResponse>({
    queryKey: ['vouchers', { pageNum, pageSize, voucherName, userEmail, resolvedUserId, isActive }],
    queryFn: async () => {
      const res = await listVouchers(queryParams);
      setQueryParams();
      return res;
    },
    refetchOnWindowFocus: false,
    enabled: shouldFetchVouchers,
  });

  const onPageChange = useCallback(
    (page: number, pageSize: number) => {
      setPageNum(page);
      setPageSize(pageSize);
      query.set('page_num', page.toString());
      query.set('page_size', pageSize.toString());
      query.set('voucher_name', voucherName);
      query.set('user_email', userEmail);
      if (isActive === 'all') {
        query.delete('is_active');
      } else {
        query.set('is_active', isActive);
      }
      window.history.pushState({}, '', `${window.location.pathname}?${query.toString()}`);
    },
    [query, voucherName, userEmail, isActive],
  );

  const vouchers = shouldFetchVouchers ? data?.result?.vouchers ?? [] : [];
  const totalCount = shouldFetchVouchers ? Number(data?.result?.total ?? 0) : 0;

  const handleDeleteVoucher = useCallback(
    async (voucher: Voucher) => {
      if (!voucher.voucherId) {
        enqueueSnackbar('Unable to delete voucher: missing voucher id.', { variant: 'error' });
        throw new Error('Missing voucher id');
      }
      try {
        await deleteVoucherMutation.mutateAsync(voucher.voucherId);
        enqueueSnackbar(`Deleted voucher ${voucher.voucherName || voucher.voucherId}.`, { variant: 'success' });
        queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      } catch (error) {
        enqueueSnackbar(`Failed to delete voucher: ${(error as Error).message}`, { variant: 'error' });
        throw error;
      }
    },
    [deleteVoucherMutation, enqueueSnackbar, queryClient],
  );

  return (
    <Container maxWidth="xl" sx={{ width: '90%' }}>
      <Helmet>
        <title>Vouchers</title>
      </Helmet>
      <Box>
        <PageSubheader title={`Vouchers [${vouchers.length} of ${totalCount}]`} />
        <VouchersTab
          voucherName={voucherName}
          setVoucherName={setVoucherName}
          userEmail={userEmail}
          setUserEmail={setUserEmail}
          isActive={isActive}
          setIsActive={setIsActive}
          data={vouchers as Array<Voucher>}
          isLoading={isLoading}
          isDeleting={deleteVoucherMutation.isPending}
          totalCount={totalCount}
          pageNum={pageNum}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onConfirmDelete={handleDeleteVoucher}
        />
      </Box>
    </Container>
  );
}
